import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import "./JobsPage.css";
import { useSearchParams, useLocation, useNavigate as useRouterNavigate } from "react-router-dom";
import Login from "../Frontend/Modal/Login/Login";
import { resolveLogoSrc } from "../../utils/logoUtils";
import JobModal from "./JobModal/JobModal";

// ─── Config ───────────────────────────────────────────────────────────────────
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

// ─── All filter options are derived dynamically from API data ─────────────────
// Only structural constants that don't represent business data are kept here

const DESKTOP_PAGE_SIZE = 9;   // 3 cols × 3 rows
const MOBILE_PAGE_SIZE = 6;   // 3 cols × 2 rows

const QUICK_FILTERS = [
  { id: "all", label: "All Jobs", icon: "🔍" },
  { id: "new", label: "New (3 days)", icon: "🆕" },
  { id: "saved", label: "Saved", icon: "❤️" },
  { id: "applied", label: "Applied", icon: "✅" },
];

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "salary-h", label: "Highest salary" },
  { value: "salary-l", label: "Lowest salary" },
  { value: "title", label: "Title A–Z" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isMobileViewport() { return window.innerWidth <= 768; }

function formatPostedDate(d) {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  const diff = Math.floor((Date.now() - dt) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff <= 6) return `${diff} days ago`;
  if (diff <= 13) return "1 week ago";
  if (diff <= 20) return "2 weeks ago";
  if (diff <= 27) return "3 weeks ago";
  return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function isNew(d) { return !!d && Math.floor((Date.now() - new Date(d)) / 86400000) <= 3; }

function parseTags(raw) {
  try {
    if (!raw) return [];
    if (typeof raw === "string") {
      return raw.startsWith("[")
        ? JSON.parse(raw)
        : raw.split(",").map(t => t.trim()).filter(Boolean);
    }
    return Array.isArray(raw) ? raw : [];
  } catch { return []; }
}

function normaliseJob(raw) {
  const tags = parseTags(raw.skills || raw.tags);
  const locLower = (raw.location || "").toLowerCase();
  const locationType = locLower.includes("hybrid")
    ? "Hybrid"
    : locLower.includes("remote") ? "Remote" : "On-site";

  const salaryMin = raw.min_salary || 0;
  const salaryMax = raw.max_salary || 0;
  const sMin = Math.round(salaryMin / 100000);
  const sMax = Math.round(salaryMax / 100000);
  const salaryAvg = sMin && sMax ? Math.round((sMin + sMax) / 2) : sMin || sMax;

  const salaryDisplay = (() => {
    const r = (raw.salary || "").replace(/^[$?₹\u20B9\s]+/, "").trim();
    if (r) return `₹${r}`;
    if (sMin && sMax) return `₹${sMin}–${sMax} LPA`;
    if (salaryAvg) return `₹${salaryAvg} LPA`;
    return "Not disclosed";
  })();

  const postedAt = raw.posted_date || raw.created_at || raw.posted_at || null;

  return {
    id: raw.id || raw._id,
    title: raw.title || "Untitled",
    company: raw.company_name || raw.company || "—",
    dept: raw.category || raw.department || "General",
    type: raw.type || raw.job_type || "Full-time",
    location: raw.location || "",
    locationType,
    salaryMin, salaryMax, salaryAvg,
    salary: salaryDisplay,
    tags,
    status: raw.status || "active",
    postedAt,
    postedLabel: formatPostedDate(postedAt),
    isNew: isNew(postedAt),
    description: raw.description || "",
    experience: raw.experience || "",
    openings: Number(raw.openings || raw.vacancies || 1),
    applicantCount: Number(raw.applicant_count || 0),
    recruiter_id: raw.recruiter_id || raw.employer_id || null,
    recruiter_email: raw.recruiter_email || raw.employer_email || null,
    logo: resolveLogoSrc(raw.company_logo || raw.ep_logo || raw.logo || ""),
  };
}

// ─── Salary buckets built dynamically from job data ──────────────────────────
function buildSalaryRanges(jobs) {
  // Always include "Any" as the first option
  const base = [{ label: "Any Salary", min: 0, max: 99999999 }];
  const maxLpa = Math.max(...jobs.map(j => Math.round(j.salaryMax / 100000) || 0), 0);
  if (maxLpa === 0) return base; // no salary data — only show "Any"

  const buckets = [];
  if (maxLpa > 0) buckets.push({ label: "0 – 3 LPA", min: 0, max: 300000 });
  if (maxLpa > 3) buckets.push({ label: "3 – 6 LPA", min: 300000, max: 600000 });
  if (maxLpa > 6) buckets.push({ label: "6 – 10 LPA", min: 600000, max: 1000000 });
  if (maxLpa > 10) buckets.push({ label: "10 – 20 LPA", min: 1000000, max: 2000000 });
  if (maxLpa > 20) buckets.push({ label: "20+ LPA", min: 2000000, max: 99999999 });

  return [...base, ...buckets];
}

// Map URL salary param → salary range label
const SALARY_PARAM_MAP = {
  "0–3 LPA": "0 – 3 LPA",
  "3–6 LPA": "3 – 6 LPA",
  "6–10 LPA": "6 – 10 LPA",
  "10–20 LPA": "10 – 20 LPA",
  "20+ LPA": "20+ LPA",
};

// ─── Auth / storage helpers ───────────────────────────────────────────────────
const TOKEN_KEYS = ["th_token", "token", "authToken"];
const USER_KEYS = ["th_user", "user", "authUser"];

function readStoredToken() {
  return TOKEN_KEYS.map(k => localStorage.getItem(k)).find(Boolean) || "";
}

function readStoredUser() {
  try {
    const raw = USER_KEYS.map(k => localStorage.getItem(k)).find(Boolean);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveUser(user, token) {
  try {
    const u = JSON.stringify(user);
    USER_KEYS.forEach(k => localStorage.setItem(k, u));
    TOKEN_KEYS.forEach(k => localStorage.setItem(k, token));
  } catch { }
}

function clearUser() {
  [...USER_KEYS, ...TOKEN_KEYS].forEach(k => {
    try { localStorage.removeItem(k); } catch { }
  });
}

function loadPersistedIds(key) {
  try {
    const r = localStorage.getItem(key);
    return r ? new Set(JSON.parse(r)) : new Set();
  } catch { return new Set(); }
}

function persistIds(key, set) {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch { }
}

// ─── Profile helpers ──────────────────────────────────────────────────────────
const REQUIRED_PROFILE_FIELDS = [
  { key: "name", label: "Full Name", icon: "👤", check: u => u.name || u.firstName || u.first_name },
  { key: "email", label: "Email Address", icon: "✉️", check: u => u.email },
  { key: "phone", label: "Phone Number", icon: "📱", check: u => u.phone || u.mobile },
  {
    key: "skills", label: "Skills", icon: "🛠️", check: u => {
      const s = u.skills;
      return Array.isArray(s) ? s.length > 0 : (typeof s === "string" ? s.trim() && s !== "[]" : !!s);
    }
  },
  { key: "resume", label: "Resume / CV", icon: "📄", check: u => u.resume_url || u.resumeUrl },
  { key: "bio", label: "Short Description", icon: "📝", check: u => u.bio || u.headline || u.description || u.about },
  { key: "aadhar", label: "Aadhaar Card Number", icon: "🪪", check: u => u.aadhar || u.aadhaar || u.aadhar_number },
  { key: "pan", label: "PAN Card Number", icon: "💳", check: u => u.pan || u.pan_card || u.pan_number },
  {
    key: "education", label: "Education Details", icon: "🎓", check: u => {
      const e = u.education || u.educations || [];
      return Array.isArray(e) ? e.length > 0 && e.some(x => x?.degree || x?.course) : !!u.degree;
    }
  },
  { key: "country", label: "Country", icon: "🌍", check: u => u.country || u.country_iso || u.countryIso },
  { key: "gender", label: "Gender", icon: "⚧️", check: u => u.gender },
];

function getMissingFields(user) {
  if (!user) return [];
  return REQUIRED_PROFILE_FIELDS.filter(f => !f.check(user));
}

function isProfileComplete(user) { return getMissingFields(user).length === 0; }

function profilePercent(user) {
  if (!user) return 0;
  const filled = REQUIRED_PROFILE_FIELDS.filter(f => f.check(user)).length;
  return Math.round((filled / REQUIRED_PROFILE_FIELDS.length) * 100);
}

function displayName(user) {
  if (!user) return "";
  return user.name
    || `${user.firstName || user.first_name || ""} ${user.lastName || user.last_name || ""}`.trim()
    || user.username || user.email || "";
}

// ─── API calls ────────────────────────────────────────────────────────────────
function authFetch(path, opts = {}) {
  const token = readStoredToken();
  return fetch(`${API_BASE}${path}`, {
    credentials: "include",
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(opts.headers || {}),
    },
  });
}

async function fetchFullProfile() {
  const token = readStoredToken();
  if (!token) return null;
  try {
    const res = await fetch(`${API_BASE}/candidate/profile`, {
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    ["skills", "locations", "education", "courses"].forEach(k => {
      if (typeof data[k] === "string") {
        try { data[k] = JSON.parse(data[k]); } catch { data[k] = []; }
      }
    });
    if (data.first_name && !data.firstName) data.firstName = data.first_name;
    if (data.last_name && !data.lastName) data.lastName = data.last_name;
    if (data.headline && !data.bio) data.bio = data.headline;
    if (data.mobile && !data.phone) data.phone = data.mobile;
    if (data.work_status && !data.experience) data.experience = data.work_status;
    if (data.country_iso && !data.country) data.country = data.country_iso;
    return data;
  } catch { return null; }
}

async function submitApplicationAPI({ job }) {
  const res = await authFetch("/applications", {
    method: "POST",
    body: JSON.stringify({
      job_id: job.id,
      recruiter_id: job.recruiter_id,
      applied_at: new Date().toISOString(),
    }),
  });
  let data = {};
  try { data = await res.json(); } catch { }
  if (res.status === 409)
    return { success: true, alreadyApplied: true, application: { job_title: job.title, company: job.company } };
  if (res.status === 422 && data.profileIncomplete)
    return { success: false, profileIncomplete: true, missing: data.missing || [], error: data.error };
  if (!res.ok)
    throw new Error(data.detail || data.error || data.message || `Server error ${res.status}`);
  return {
    success: true,
    alreadyApplied: false,
    application: {
      job_title: data.application?.job_title || data.job_title || job.title,
      company: data.application?.company || data.company || job.company,
      id: data.application?.id || data.id || null,
    },
  };
}

async function sendEmailsAPI({ user, job }) {
  try {
    await authFetch("/email/job-application", {
      method: "POST",
      body: JSON.stringify({
        admin_email: process.env.REACT_APP_ADMIN_EMAIL || "",
        user_email: user.email,
        user_name: displayName(user),
        user_phone: user.phone || user.mobile || "N/A",
        user_skills: Array.isArray(user.skills) ? user.skills.join(", ") : (user.skills || "N/A"),
        user_experience: user.experience || user.work_status || "N/A",
        user_resume: user.resume_url || "N/A",
        job_title: job.title,
        job_company: job.company,
        job_id: job.id,
        recruiter_id: job.recruiter_id,
        recruiter_email: job.recruiter_email,
        applied_at: new Date().toISOString(),
      }),
    });
  } catch (e) { console.warn("Email notification failed (non-fatal):", e.message); }
}

async function toggleSaveJobAPI(jobId, saving) {
  try {
    await authFetch("/saved-jobs", {
      method: saving ? "POST" : "DELETE",
      body: JSON.stringify({ job_id: jobId }),
    });
  } catch (e) { console.warn("Save job API error:", e.message); }
}

// ═════════════════════════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ═════════════════════════════════════════════════════════════════════════════

// ─── Profile Incomplete Popup ─────────────────────────────────────────────────
function ProfileIncompletePopup({ open, onClose, user, targetJob, serverMissing }) {
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  if (!open) return null;

  const LABEL_MAP = Object.fromEntries(
    REQUIRED_PROFILE_FIELDS.map(f => [f.label, { key: f.key, icon: f.icon }])
  );

  const missing = serverMissing?.length > 0
    ? serverMissing.map(l => ({ ...(LABEL_MAP[l] || { key: l, icon: "❗" }), label: l }))
    : getMissingFields(user);

  const pct = profilePercent(user);
  const totalFields = REQUIRED_PROFILE_FIELDS.length;

  return (
    <div className="pip-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pip-modal" role="dialog" aria-modal="true">
        <div className="pip-accent-bar" />
        <button className="pip-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="pip-header">
          <div className="pip-header__icon-wrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="32" height="32">
              <circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
          </div>
          <div className="pip-header__text">
            <h2 className="pip-header__title">Profile Incomplete</h2>
            <p className="pip-header__sub">
              {targetJob
                ? <>To apply for <strong>{targetJob.title}</strong> at <strong>{targetJob.company}</strong>, complete the missing details first.</>
                : "Fill in the required details to start applying for jobs."}
            </p>
          </div>
        </div>
        <div className="pip-progress">
          <div className="pip-progress__row">
            <span className="pip-progress__label">Profile Strength</span>
            <span className="pip-progress__pct" style={{ color: pct < 50 ? "var(--brand-500)" : pct < 80 ? "var(--brand-600)" : "var(--ink-900)" }}>{pct}%</span>
          </div>
          <div className="pip-progress__track">
            <div className="pip-progress__fill" style={{ width: `${pct}%`, background: pct < 50 ? "var(--brand-500)" : pct < 80 ? "var(--brand-600)" : "var(--ink-900)" }} />
          </div>
          <p className="pip-progress__note">{totalFields - missing.length} of {totalFields} required fields filled</p>
        </div>
        <div className="pip-body">
          <p className="pip-body__heading">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Missing fields
          </p>
          <div className="pip-fields-list">
            {missing.map((f, i) => (
              <div key={i} className="pip-field-row">
                <div className="pip-field-row__left">
                  <span className="pip-field-row__icon">{f.icon}</span>
                  <span className="pip-field-row__label">{f.label}</span>
                </div>
                <span className="pip-field-row__tag">Required</span>
              </div>
            ))}
          </div>
        </div>
        <div className="pip-footer">
          <a href="/employee/profile" className="pip-btn pip-btn--primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            Complete My Profile
          </a>
          <button className="pip-btn pip-btn--ghost" onClick={onClose}>Maybe Later</button>
        </div>
      </div>
    </div>
  );
}

// ─── Success Popup ────────────────────────────────────────────────────────────
function SuccessPopup({ open, job, onClose }) {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    const t = setTimeout(onClose, 10000);
    return () => { clearTimeout(t); document.body.style.overflow = ""; };
  }, [open, onClose]);

  if (!open || !job) return null;

  const confettiColors = [
    "var(--brand-500)", "var(--brand-600)", "var(--ink-900)",
    "var(--brand-700)", "var(--brand-400)", "var(--brand-200)",
  ];

  return (
    <div className="jp-success-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="jp-success-modal">
        <div className="jp-success__confetti" aria-hidden="true">
          {Array.from({ length: 18 }, (_, i) => (
            <div key={i} className="jp-success__piece"
              style={{ "--i": i, "--c": confettiColors[i % confettiColors.length] }} />
          ))}
        </div>
        <button className="jp-success__close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="18" height="18">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="jp-success__top">
          <div className="jp-success__emoji">🎉</div>
          <div className="jp-success__check">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="22" height="22">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        </div>
        <div className="jp-success__content">
          <h3 className="jp-success__title">Application Submitted!</h3>
          <p className="jp-success__desc">
            You've successfully applied for <strong>{job.title || job.job_title}</strong> at <strong>{job.company}</strong>.
          </p>
          <p className="jp-success__note">The recruiter will review your profile and reach out if you're shortlisted.</p>
        </div>
        <div className="jp-success__actions">
          <a href="/employee/profile" className="jp-success__btn jp-success__btn--primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
            </svg>
            View My Applications
          </a>
          <a href="/employee/profile" className="jp-success__btn jp-success__btn--outline">Complete Profile</a>
        </div>
        <div className="jp-success__bar" />
      </div>
    </div>
  );
}

// ─── Toasts ───────────────────────────────────────────────────────────────────
function ErrorToast({ open, message, onClose }) {
  useEffect(() => {
    if (open) { const t = setTimeout(onClose, 7000); return () => clearTimeout(t); }
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="jp-error-toast">
      <span className="jp-error-toast__icon">⚠️</span>
      <span className="jp-error-toast__msg">{message || "Something went wrong. Please try again."}</span>
      <button onClick={onClose} className="jp-error-toast__close">✕</button>
    </div>
  );
}

function SaveToast({ open, saved, onClose }) {
  useEffect(() => {
    if (open) { const t = setTimeout(onClose, 3200); return () => clearTimeout(t); }
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="jp-toast">
      <span>{saved ? "🔖" : "📌"}</span>
      <span>{saved ? "Job saved to your list" : "Removed from saved jobs"}</span>
      <button onClick={onClose}>✕</button>
    </div>
  );
}

// ─── JobCard ──────────────────────────────────────────────────────────────────
function JobCard({ job, savedJobs, appliedJobs, applyingJobId, onToggleSave, onApplyClick, onViewDesc }) {
  const isSaved = savedJobs.has(job.id);
  const isApplied = appliedJobs.has(job.id);
  const isApplying = applyingJobId === job.id;
  const initial = (job.company || "?")[0].toUpperCase();

  return (
    <div className={`jpc${isApplied ? " jpc--applied" : ""}`}>
      <div className="jpc__stripe" />

      <button
        className={`jpc__bmark${isSaved ? " jpc__bmark--on" : ""}`}
        onClick={e => { e.stopPropagation(); onToggleSave(job); }}
        title={isSaved ? "Remove saved" : "Save job"}
        aria-label={isSaved ? "Remove from saved" : "Save job"}
      >
        <svg viewBox="0 0 24 24" fill={isSaved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.2" width="16" height="16">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      <div className="jpc__header">
        <div className="jpc__logo">
          {job.logo
            ? <img src={job.logo} alt={job.company} style={{ width: "100%", height: "100%", objectFit: "contain", borderRadius: 8 }} />
            : <span className="jpc__logo-initial">{initial}</span>}
        </div>
        <div className="jpc__header-info">
          <div className="jpc__company">{job.company}</div>
          {job.postedLabel && (
            <div className={`jpc__posted-date${job.isNew ? " jpc__posted-date--new" : ""}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              {job.isNew && <span className="jpc__new-dot" />}
              {job.postedLabel}
            </div>
          )}
        </div>
        {isApplied
          ? <span className="jpc__status jpc__status--applied">✓ Applied</span>
          : job.status === "active"
            ? <span className="jpc__status jpc__status--active">● Hiring</span>
            : <span className="jpc__status jpc__status--paused">⏸ Paused</span>}
      </div>

      <h3 className="jpc__title">{job.title}</h3>
      <span className="jpc__dept">{job.dept}</span>

      <div className="jpc__meta">
        {job.location && (
          <span className="jpc__chip jpc__chip--loc">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
            </svg>
            {job.location}
          </span>
        )}
        <span className="jpc__chip jpc__chip--type">{job.type}</span>
        {job.locationType !== "On-site" && (
          <span className={`jpc__chip${job.locationType === "Remote" ? " jpc__chip--remote" : " jpc__chip--hybrid"}`}>
            {job.locationType === "Remote" ? "🌐 Remote" : "🔄 Hybrid"}
          </span>
        )}
        {job.experience && (
          <span className="jpc__chip jpc__chip--exp">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="10" height="10">
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
            {job.experience}
          </span>
        )}
      </div>

      {job.salary !== "Not disclosed" && (
        <div className="jpc__salary">{job.salary}</div>
      )}

      {job.tags.length > 0 && (
        <div className="jpc__tags">
          {job.tags.slice(0, 4).map((t, i) => <span key={i} className="jpc__tag">{t}</span>)}
          {job.tags.length > 4 && <span className="jpc__tag jpc__tag--more">+{job.tags.length - 4}</span>}
        </div>
      )}

      <div className="jpc__info-bar">
        <span className="jpc__vacancy">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          {job.openings === 1 ? "1 vacancy" : `${job.openings} vacancies`}
        </span>
        {job.postedLabel && (
          <>
            <span className="jpc__info-sep">·</span>
            <span className={`jpc__date-label${job.isNew ? " jpc__date-label--new" : ""}`}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
              Posted {job.postedLabel}
            </span>
          </>
        )}
      </div>

      <div className="jpc__actions">
        <button className="jpc__view" onClick={() => onViewDesc(job)}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
          </svg>
          View Details
        </button>
        {isApplied ? (
          <button className="jpc__apply jpc__apply--done" disabled>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="12" height="12">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Applied
          </button>
        ) : isApplying ? (
          <button className="jpc__apply" disabled>
            <span className="jpc__spinner" />Applying…
          </button>
        ) : (
          <button className="jpc__apply" onClick={() => onApplyClick(job)} disabled={job.status === "paused"}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
            Apply Now
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Active Filter Chip ───────────────────────────────────────────────────────
function ActiveChip({ label, onRemove }) {
  return (
    <span className="jp-active-chip">
      {label}
      <button onClick={onRemove} aria-label={`Remove ${label}`}>✕</button>
    </span>
  );
}

// ─── User Badge / Dropdown ────────────────────────────────────────────────────
function UserBadge({ user, onLogout, onCompleteProfile }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const pct = profilePercent(user);
  const complete = isProfileComplete(user);
  const barColor = pct < 50 ? "var(--brand-500)" : pct < 80 ? "var(--brand-600)" : "var(--ink-900)";

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="jp-user-badge" ref={ref}>
      <button className="jp-user-badge__btn" onClick={() => setOpen(o => !o)}>
        <div className="jp-user-badge__avatar">{(displayName(user) || "U")[0].toUpperCase()}</div>
        <div className="jp-user-badge__info">
          <span className="jp-user-badge__name">{displayName(user)}</span>
          <span className="jp-user-badge__pct">{pct}% complete</span>
        </div>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="jp-user-badge__drop">
          <div className="jp-user-badge__drop-head">
            <div className="jp-user-badge__drop-avatar">{(displayName(user) || "U")[0].toUpperCase()}</div>
            <div>
              <div className="jp-user-badge__drop-name">{displayName(user)}</div>
              <div className="jp-user-badge__drop-email">{user.email}</div>
            </div>
          </div>
          <div className="jp-user-badge__drop-bar-wrap">
            <div className="jp-user-badge__drop-bar-label">
              <span>Profile strength</span><span>{pct}%</span>
            </div>
            <div className="jp-user-badge__drop-bar-track">
              <div className="jp-user-badge__drop-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
            </div>
          </div>
          <div className="jp-user-badge__drop-actions">
            {!complete && (
              <button className="jp-user-badge__drop-btn jp-user-badge__drop-btn--warn"
                onClick={() => { setOpen(false); onCompleteProfile(); }}>
                ⚠️ Complete profile to apply
              </button>
            )}
            <a href="/employee/profile" className="jp-user-badge__drop-btn">👤 View Profile</a>
            <button className="jp-user-badge__drop-btn jp-user-badge__drop-btn--logout"
              onClick={() => { setOpen(false); onLogout(); }}>
              🚪 Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sidebar Section ──────────────────────────────────────────────────────────
function SidebarSection({ title, icon, children, count }) {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className={`jp-sidebar-section${collapsed ? " jp-sidebar-section--collapsed" : ""}`}>
      <button className="jp-sidebar-section__header" onClick={() => setCollapsed(c => !c)}>
        <span className="jp-sidebar-section__title-wrap">
          {icon && <span className="jp-sidebar-section__icon">{icon}</span>}
          <span className="jp-sidebar-section__title">{title}</span>
          {count !== undefined && count > 0 && (
            <span className="jp-sidebar-section__badge">{count}</span>
          )}
        </span>
        <svg
          className="jp-sidebar-section__chevron"
          width="12" height="12" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2.5"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {!collapsed && <div className="jp-sidebar-section__body">{children}</div>}
    </div>
  );
}

// ─── See More Button ──────────────────────────────────────────────────────────
function SeeMoreButton({ remaining, onLoadMore, loading }) {
  if (remaining <= 0) return null;
  return (
    <div className="jp-see-more-wrap">
      <button className="jp-see-more-btn" onClick={onLoadMore} disabled={loading}>
        {loading ? (
          <><span className="jp-see-more-spinner" />Loading…</>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16" height="16">
              <polyline points="6 9 12 15 18 9" />
            </svg>
            See More
          </>
        )}
      </button>
    </div>
  );
}

// ─── Sidebar Search Box ───────────────────────────────────────────────────────
function SidebarSearch({ value, onChange, placeholder }) {
  return (
    <div className="jp-keyword-box">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
      </svg>
      <input
        type="text"
        placeholder={placeholder || "Search…"}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
      {value && (
        <button className="jp-keyword-box__clear" onClick={() => onChange("")} aria-label="Clear">✕</button>
      )}
    </div>
  );
}

// ─── CheckList ───────────────────────────────────────────────────────────────
function CheckList({ items, selected, onToggle, getCount }) {
  return (
    <div className="jp-check-list">
      {items.map(item => (
        <label key={item} className="jp-check-item">
          <input type="checkbox" checked={selected.has(item)} onChange={() => onToggle(item)} />
          <span>{item}</span>
          {getCount && <span className="jp-check-count">{getCount(item)}</span>}
        </label>
      ))}
    </div>
  );
}

// ─── RadioList ───────────────────────────────────────────────────────────────
function RadioList({ items, selected, onSelect, getName }) {
  return (
    <div className="jp-radio-list">
      {items.map((item, i) => {
        const val = typeof item === "object" ? item.value ?? i : item;
        const name = typeof item === "object" ? (getName ? getName(item) : item.label) : item;
        return (
          <label key={val} className={`jp-radio-item${selected === val ? " jp-radio-item--on" : ""}`}>
            <input type="radio" checked={selected === val} onChange={() => onSelect(val)} />
            {name}
          </label>
        );
      })}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═════════════════════════════════════════════════════════════════════════════
export default function JobsPage() {
  const [searchParams] = useSearchParams();
  const routerLocation = useLocation();
  const urlParams = new URLSearchParams(routerLocation.search);
const routerNav = useRouterNavigate();
  // URL params
const companyParam = searchParams.get("company") || urlParams.get("company") || "";
  const keywordParam = searchParams.get("keyword") || "";
  const locationParam = searchParams.get("location") || "";
  const jobTypeParam = searchParams.get("jobType") || "";
  const experienceParam = searchParams.get("experience") || "";
  const salaryParam = searchParams.get("salary") || "";
  const skillsParam = searchParams.get("skills") || "";

  // Data state
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [user, setUser] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [savedJobs, setSavedJobs] = useState(() => loadPersistedIds("jp_saved"));
  const [appliedJobs, setAppliedJobs] = useState(() => loadPersistedIds("jp_applied"));
  const [applyingJobId, setApplyingJobId] = useState(null);
  const applyingRef = useRef(new Set());

  // Filter state — all driven by job data, no hardcoded lists
  const [sort, setSort] = useState("newest");
  const [quickFilter, setQuickFilter] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [depts, setDepts] = useState(new Set());
  const [types, setTypes] = useState(new Set());
  const [locTypes, setLocTypes] = useState(new Set());
  const [cityFilter, setCityFilter] = useState("");
  const [salaryRangeIdx, setSalaryRangeIdx] = useState(0); // index into dynamic salary ranges
  const [activeTags, setActiveTags] = useState(new Set());
  const [experience, setExperience] = useState("");

  // UI state
  const [loginDrawerOpen, setLoginDrawerOpen] = useState(false);
  const [profilePopupOpen, setProfilePopupOpen] = useState(false);
  const [pendingJob, setPendingJob] = useState(null);
  const [serverMissing, setServerMissing] = useState([]);
  const [successData, setSuccessData] = useState({ open: false, job: null });
  const [toastData, setToastData] = useState({ open: false, saved: false });
  const [errorToast, setErrorToast] = useState({ open: false, message: "" });

  // Pagination
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [visibleCount, setVisibleCount] = useState(() => isMobileViewport() ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE);
  const [seeMoreLoading, setSeeMoreLoading] = useState(false);
const removeUrlParam = useCallback((param) => {
  const url = new URL(window.location.href);
  url.searchParams.delete(param);
  routerNav(url.pathname + url.search, { replace: true });
}, [routerNav]);
  // ── Viewport tracking ─────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(isMobileViewport());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const resetVisible = useCallback(() => {
    setVisibleCount(isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE);
  }, [isMobile]);

  // ── Persist ids ───────────────────────────────────────────────────────────
  useEffect(() => { persistIds("jp_applied", appliedJobs); }, [appliedJobs]);
  useEffect(() => { persistIds("jp_saved", savedJobs); }, [savedJobs]);

  // ── Bootstrap user ────────────────────────────────────────────────────────
  useEffect(() => {
    const stored = readStoredUser();
    if (stored) {
      setUser(stored);
      fetchFullProfile().then(full => {
        if (!full) return;
        const merged = { ...stored, ...full };
        saveUser(merged, readStoredToken());
        setUser(merged);
      });
    }
    const onStorage = () => { const f = readStoredUser(); setUser(f || null); };
    const onAuthUpdate = () => {
      const f = readStoredUser();
      if (f) {
        setUser(f);
        fetchFullProfile().then(full => {
          if (!full) return;
          const m = { ...f, ...full };
          saveUser(m, readStoredToken());
          setUser(m);
        });
      }
    };
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth:update", onAuthUpdate);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth:update", onAuthUpdate);
    };
  }, []);

  // ── Fetch jobs ────────────────────────────────────────────────────────────
  const fetchJobs = useCallback(async () => {
    setLoading(true); setFetchError(null);
    try {
      const res = await fetch(`${API_BASE}/jobs/browse`);
      if (!res.ok) {
        const errText = await res.text();
        if (errText.trim().startsWith("<")) throw new Error("API URL misconfigured — got HTML instead of JSON");
        throw new Error(`Server ${res.status}`);
      }
      const raw = await res.json();
      const rows = Array.isArray(raw) ? raw : (raw.data || raw.jobs || []);
      setAllJobs(rows.map(normaliseJob));
    } catch (err) {
      console.error("fetchJobs error:", err.message);
      setFetchError("Could not load jobs. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // ── Sync saved/applied from server ───────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    authFetch("/saved-jobs").then(r => r.ok ? r.json() : null).then(d => {
      if (d?.success) {
        const ids = new Set((d.data || []).map(j => j.job_id || j.id));
        setSavedJobs(prev => { const m = new Set([...prev, ...ids]); persistIds("jp_saved", m); return m; });
      }
    }).catch(() => { });
    authFetch("/applications/mine").then(r => r.ok ? r.json() : null).then(d => {
      if (d?.success) {
        const ids = new Set((d.data || []).map(j => j.job_id || j.id));
        setAppliedJobs(prev => { const m = new Set([...prev, ...ids]); persistIds("jp_applied", m); return m; });
      }
    }).catch(() => { });
  }, [user]);

  // ── Derive dynamic filter options from API data ───────────────────────────
  const departments = useMemo(() => [...new Set(allJobs.map(j => j.dept).filter(Boolean))].sort(), [allJobs]);
  const jobTypes = useMemo(() => [...new Set(allJobs.map(j => j.type).filter(Boolean))].sort(), [allJobs]);
  const locationTypes = useMemo(() => [...new Set(allJobs.map(j => j.locationType).filter(Boolean))].sort(), [allJobs]);
  const allTags = useMemo(() => [...new Set(allJobs.flatMap(j => j.tags))].sort().slice(0, 30), [allJobs]);
  const experienceOpts = useMemo(() => {
    const base = [""];
    const vals = [...new Set(allJobs.map(j => j.experience).filter(Boolean))].sort();
    return [...base, ...vals];
  }, [allJobs]);
  const salaryRanges = useMemo(() => buildSalaryRanges(allJobs), [allJobs]);

  // ── Seed filters from URL params ──────────────────────────────────────────
  useEffect(() => {
    if (keywordParam) setKeyword(keywordParam);
    if (locationParam) {
      const lc = locationParam.toLowerCase();
      if (lc === "remote") setLocTypes(new Set(["Remote"]));
      else if (lc === "hybrid") setLocTypes(new Set(["Hybrid"]));
      else setCityFilter(locationParam);
    }
    if (jobTypeParam) {
      // Match against dynamic types when available
      setTypes(new Set([jobTypeParam]));
    }
    if (salaryParam) {
      const label = SALARY_PARAM_MAP[salaryParam];
      const idx = salaryRanges.findIndex(r => r.label === label);
      if (idx > 0) setSalaryRangeIdx(idx);
    }
    if (experienceParam) setExperience(experienceParam);
    if (skillsParam) setActiveTags(new Set([skillsParam]));
  }, [keywordParam, locationParam, jobTypeParam, experienceParam, salaryParam, skillsParam, salaryRanges]); // eslint-disable-line

  // ── Filtered + sorted list ────────────────────────────────────────────────
  const filteredJobs = useMemo(() => {
    const range = salaryRanges[salaryRangeIdx] || salaryRanges[0];
const res = allJobs.filter(job => {
  if (quickFilter === "new"     && !job.isNew)               return false;
  if (quickFilter === "saved"   && !savedJobs.has(job.id))   return false;
  if (quickFilter === "applied" && !appliedJobs.has(job.id)) return false;
  // if (companyParam && !job.company.toLowerCase().includes(companyParam.toLowerCase())) return false;

  // ← keyword block removed entirely — scoring in sort handles it

  if (depts.size    > 0 && !depts.has(job.dept))     return false;
  if (types.size    > 0 && !types.has(job.type))     return false;
  if (locTypes.size > 0) {
    const ok = [...locTypes].some(l => {
      if (l === "Remote")  return job.locationType === "Remote";
      if (l === "Hybrid")  return job.locationType === "Hybrid";
      if (l === "On-site") return job.locationType === "On-site";
      return false;
    });
    if (!ok) return false;
  }
  if (cityFilter && !job.location.toLowerCase().includes(cityFilter.toLowerCase())) return false;
  if (activeTags.size > 0 && ![...activeTags].some(t => job.tags.some(jt => jt.toLowerCase().includes(t.toLowerCase())))) return false;
  if (salaryRangeIdx > 0 && (job.salaryMin > 0 || job.salaryMax > 0)) {
    if (job.salaryMax < range.min || job.salaryMin > range.max) return false;
  }
  if (experience) {
    const jobExp = (job.experience || "").toLowerCase();
    if (jobExp && !jobExp.includes(experience.toLowerCase())) return false;
  }
  return true;
});

const kw = keyword.trim().toLowerCase();
const cp = companyParam.trim().toLowerCase();

return [...res].sort((a, b) => {
  const scoreJob = (job) => {
    let score = 0;

    // Company param boost (from URL ?company=infosys)
    if (cp && job.company.toLowerCase().includes(cp)) score += 200;

    // Keyword boost (from search box)
    if (kw) {
      if (job.company.toLowerCase().includes(kw)) score += 100;
      if (job.title.toLowerCase().includes(kw))   score += 50;
      if (job.dept.toLowerCase().includes(kw))    score += 20;
      if (job.tags.some(t => t.toLowerCase().includes(kw))) score += 10;
    }

    return score;
  };

  const diff = scoreJob(b) - scoreJob(a);
  if (diff !== 0) return diff;

  if (sort === "newest")   return new Date(b.postedAt || 0) - new Date(a.postedAt || 0);
  if (sort === "salary-h") return (b.salaryAvg || 0) - (a.salaryAvg || 0);
  if (sort === "salary-l") return (a.salaryAvg || 0) - (b.salaryAvg || 0);
  if (sort === "title")    return a.title.localeCompare(b.title);
  return 0;
});
  }, [allJobs, keyword, depts, types, locTypes, cityFilter, activeTags, salaryRangeIdx, salaryRanges, experience, sort, companyParam, quickFilter, savedJobs, appliedJobs]);

  useEffect(() => { resetVisible(); }, [filteredJobs, resetVisible]); // eslint-disable-line

  // ── Pagination ────────────────────────────────────────────────────────────
  const visibleJobs = useMemo(() => filteredJobs.slice(0, visibleCount), [filteredJobs, visibleCount]);
  const remaining = filteredJobs.length - visibleCount;
  const hasMore = remaining > 0;

  const handleLoadMore = useCallback(() => {
    if (seeMoreLoading || !hasMore) return;
    setSeeMoreLoading(true);
    setTimeout(() => {
      setVisibleCount(c => c + (isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE));
      setSeeMoreLoading(false);
    }, 300);
  }, [seeMoreLoading, hasMore, isMobile]);

  // ── Filter helpers ────────────────────────────────────────────────────────
  const toggleSet = (setter, val) => setter(prev => {
    const n = new Set(prev);
    n.has(val) ? n.delete(val) : n.add(val);
    return n;
  });

const clearAll = () => {
  setKeyword(""); setDepts(new Set()); setTypes(new Set()); setLocTypes(new Set());
  setCityFilter(""); setSalaryRangeIdx(0); setActiveTags(new Set()); setExperience(""); setQuickFilter("all");
  if (companyParam) removeUrlParam("company");
};

  // ── Active filter chips ───────────────────────────────────────────────────
const activeFilters = [
  keyword    && { label: `"${keyword}"`,        clear: () => setKeyword("") },
companyParam && { label: `🏢 ${companyParam}`, clear: () => removeUrlParam("company") },
  cityFilter && { label: `📍 ${cityFilter}`,    clear: () => setCityFilter("") },
  experience && { label: `🎓 ${experience}`,    clear: () => setExperience("") },
  salaryRangeIdx > 0 && { label: salaryRanges[salaryRangeIdx]?.label, clear: () => setSalaryRangeIdx(0) },
  ...[...depts].map(d      => ({ label: d,          clear: () => toggleSet(setDepts,     d) })),
  ...[...types].map(t      => ({ label: t,           clear: () => toggleSet(setTypes,     t) })),
  ...[...locTypes].map(l   => ({ label: l,           clear: () => toggleSet(setLocTypes,  l) })),
  ...[...activeTags].map(t => ({ label: `🔧 ${t}`,  clear: () => toggleSet(setActiveTags, t) })),
].filter(Boolean);

  // ── Counts for quick filter badges ───────────────────────────────────────
  const newCount = allJobs.filter(j => j.isNew).length;
  const savedCount = allJobs.filter(j => savedJobs.has(j.id)).length;
  const appliedCount = allJobs.filter(j => appliedJobs.has(j.id)).length;
  const quickCounts = { all: allJobs.length, new: newCount, saved: savedCount, applied: appliedCount };

  // ── Apply flow ────────────────────────────────────────────────────────────
  const executeApply = useCallback(async (job, currentUser) => {
    if (!job || !currentUser) return;
    if (applyingRef.current.has(job.id)) return;

    let alreadyApplied = false;
    setAppliedJobs(cur => { alreadyApplied = cur.has(job.id); return cur; });
    if (alreadyApplied) {
      setSuccessData({ open: true, job: { title: job.title, company: job.company, id: job.id } });
      return;
    }

    applyingRef.current.add(job.id);
    setApplyingJobId(job.id);

    try {
      const fresh = await fetchFullProfile();
      if (fresh) {
        const merged = { ...currentUser, ...fresh };
        saveUser(merged, readStoredToken());
        setUser(merged);
        currentUser = merged; // eslint-disable-line no-param-reassign
      }

      const result = await submitApplicationAPI({ job });

      if (result.alreadyApplied) {
        setAppliedJobs(prev => { const n = new Set([...prev, job.id]); persistIds("jp_applied", n); return n; });
        setSuccessData({ open: true, job: { title: job.title, company: job.company, id: job.id } });
        return;
      }
      if (result.profileIncomplete) {
        setPendingJob(job); setServerMissing(result.missing || []); setProfilePopupOpen(true);
        return;
      }
      if (!result.success) {
        setErrorToast({ open: true, message: result.error || "Application failed." });
        return;
      }

      setAppliedJobs(prev => { const n = new Set([...prev, job.id]); persistIds("jp_applied", n); return n; });
      setSuccessData({ open: true, job: { title: result.application?.job_title || job.title, company: result.application?.company || job.company, id: job.id } });
      sendEmailsAPI({ user: currentUser, job });

    } catch (err) {
      let msg = err.message || "Network error. Please check your connection.";
      if (msg.includes("401") || msg.includes("Unauthorized")) msg = "Session expired. Please log in again.";
      else if (msg.includes("422")) msg = "Profile incomplete. Please fill all required fields.";
      else if (msg.includes("500")) msg = "Server error. Please try again in a moment.";
      setErrorToast({ open: true, message: msg });
    } finally {
      applyingRef.current.delete(job.id);
      setApplyingJobId(null);
    }
  }, []);

  const handleApplyClick = useCallback(job => {
    if (appliedJobs.has(job.id)) return;
    if (!user) { setPendingJob(job); setLoginDrawerOpen(true); return; }
    executeApply(job, user);
  }, [user, appliedJobs, executeApply]);

  const handleLoginSuccess = useCallback(({ token, user: loggedUser }) => {
    saveUser(loggedUser, token);
    setUser(loggedUser);
    fetchFullProfile().then(full => {
      const merged = full ? { ...loggedUser, ...full } : loggedUser;
      saveUser(merged, token);
      setUser(merged);
      setLoginDrawerOpen(false);
      if (!pendingJob) return;
      const job = pendingJob; setPendingJob(null); executeApply(job, merged);
    }).catch(() => {
      setLoginDrawerOpen(false);
      if (!pendingJob) return;
      const job = pendingJob; setPendingJob(null); executeApply(job, loggedUser);
    });
  }, [pendingJob, executeApply]);

  const handleToggleSave = useCallback(job => {
    if (!user) { setPendingJob(null); setLoginDrawerOpen(true); return; }
    const saving = !savedJobs.has(job.id);
    setSavedJobs(prev => { const n = new Set(prev); saving ? n.add(job.id) : n.delete(job.id); persistIds("jp_saved", n); return n; });
    toggleSaveJobAPI(job.id, saving);
    setToastData({ open: true, saved: saving });
  }, [user, savedJobs]);

  const handleLogout = useCallback(() => {
    clearUser(); setUser(null);
    setSavedJobs(new Set()); persistIds("jp_saved", new Set());
    setAppliedJobs(new Set()); persistIds("jp_applied", new Set());
    setPendingJob(null); setQuickFilter("all");
    applyingRef.current.clear(); setApplyingJobId(null);
  }, []);

  const handleProfilePopupClose = useCallback(() => {
    setProfilePopupOpen(false); setPendingJob(null); setServerMissing([]);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="jp-root main-content">

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside className="jp-sidebar">
        <div className="jp-sidebar__inner">

          {/* Header */}
          <div className="jp-sidebar__head">
            <div className="jp-sidebar__head-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                <path d="M3 4h18M7 12h10M11 20h2" />
              </svg>
              Filters
            </div>
            {(activeFilters.length > 0 || quickFilter !== "all") && (
              <button className="jp-sidebar__reset" onClick={clearAll}>
                ✕ Reset all
              </button>
            )}
          </div>



          {/* Quick filters — only when logged in */}
          {user && (
            <SidebarSection title="My Jobs" icon="👤">
              <div className="jp-quick-filters">
                {QUICK_FILTERS.map(f => (
                  <button
                    key={f.id}
                    className={`jp-quick-btn${quickFilter === f.id ? " jp-quick-btn--on" : ""}`}
                    onClick={() => setQuickFilter(f.id)}
                  >
                    <span className="jp-quick-btn__label">{f.icon} {f.label}</span>
                    <span className="jp-quick-btn__count">{quickCounts[f.id]}</span>
                  </button>
                ))}
              </div>
            </SidebarSection>
          )}

          {/* Keyword search */}
          <SidebarSection title="Search" icon="🔍">
            <SidebarSearch
              value={keyword}
              onChange={setKeyword}
              placeholder="Title, company, skill…"
            />
          </SidebarSection>

          {/* Department — dynamic */}
          {departments.length > 0 && (
            <SidebarSection
              title="Department"
              icon="🏢"
              count={depts.size}
            >
              <CheckList
                items={departments}
                selected={depts}
                onToggle={v => toggleSet(setDepts, v)}
                getCount={d => allJobs.filter(j => j.dept === d).length}
              />
            </SidebarSection>
          )}

          {/* Job Type — dynamic */}
          {jobTypes.length > 0 && (
            <SidebarSection
              title="Job Type"
              icon="💼"
              count={types.size}
            >
              <CheckList
                items={jobTypes}
                selected={types}
                onToggle={v => toggleSet(setTypes, v)}
                getCount={t => allJobs.filter(j => j.type === t).length}
              />
            </SidebarSection>
          )}

          {/* Work Mode — dynamic */}
          {locationTypes.length > 0 && (
            <SidebarSection
              title="Work Mode"
              icon="🌐"
              count={locTypes.size}
            >
              <CheckList
                items={locationTypes}
                selected={locTypes}
                onToggle={v => toggleSet(setLocTypes, v)}
                getCount={l => allJobs.filter(j => j.locationType === l).length}
              />
            </SidebarSection>
          )}

          {/* Salary — dynamic buckets */}
          {salaryRanges.length > 1 && (
            <SidebarSection
              title="Salary Range"
              icon="💰"
              count={salaryRangeIdx > 0 ? 1 : 0}
            >
              <RadioList
                items={salaryRanges.map((r, i) => ({ value: i, label: r.label }))}
                selected={salaryRangeIdx}
                onSelect={setSalaryRangeIdx}
                getName={item => item.label}
              />
            </SidebarSection>
          )}

          {/* Experience — dynamic */}
          {experienceOpts.length > 1 && (
            <SidebarSection
              title="Experience"
              icon="🎓"
              count={experience ? 1 : 0}
            >
              <RadioList
                items={experienceOpts.map(e => ({ value: e, label: e || "Any" }))}
                selected={experience}
                onSelect={setExperience}
                getName={item => item.label}
              />
            </SidebarSection>
          )}

          {/* Skills — dynamic */}
          {allTags.length > 0 && (
            <SidebarSection
              title="Skills"
              icon="🛠️"
              count={activeTags.size}
            >
              <div className="jp-tag-filter-wrap">
                {allTags.map(t => (
                  <button
                    key={t}
                    className={`jp-tag-filter-btn${activeTags.has(t) ? " jp-tag-filter-btn--on" : ""}`}
                    onClick={() => toggleSet(setActiveTags, t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </SidebarSection>
          )}

          {/* Clear all footer */}
          {(activeFilters.length > 0 || quickFilter !== "all") && (
            <button className="jp-sidebar-clear" onClick={clearAll}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 .49-4.7" />
              </svg>
              Clear all filters
            </button>
          )}
        </div>
      </aside>

      {/* ── Main ─────────────────────────────────────────────────────────── */}
      <main className="jp-main">

        {/* Topbar */}
        <div className="jp-topbar">
          <div className="jp-topbar__left">
            <span className="jp-result-count">
              {loading
                ? "Loading…"
                : <><strong>{visibleJobs.length}</strong> of <strong>{filteredJobs.length}</strong> job{filteredJobs.length !== 1 ? "s" : ""}</>}
              {!loading && filteredJobs.length < allJobs.length && (
                <span className="jp-result-total"> (filtered from {allJobs.length})</span>
              )}
            </span>
            {companyParam && <span className="jp-company-badge">🏢 {companyParam}</span>}
            {quickFilter !== "all" && (
              <span className="jp-quick-badge">
                {QUICK_FILTERS.find(f => f.id === quickFilter)?.icon}{" "}
                {QUICK_FILTERS.find(f => f.id === quickFilter)?.label}
                <button onClick={() => setQuickFilter("all")} aria-label="Clear quick filter">✕</button>
              </span>
            )}
            {user && !isProfileComplete(user) && (
              <button className="jp-nudge" onClick={() => { setPendingJob(null); setServerMissing([]); setProfilePopupOpen(true); }}>
                ⚠️ Complete profile to apply
              </button>
            )}
          </div>
          <div className="jp-topbar__right">
            {user && (
              <UserBadge
                user={user}
                onLogout={handleLogout}
                onCompleteProfile={() => { setPendingJob(null); setServerMissing([]); setProfilePopupOpen(true); }}
              />
            )}
            <select className="jp-sort-select" value={sort} onChange={e => setSort(e.target.value)}>
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <div className="jp-active-chips">
            {activeFilters.map((f, i) => (
              <ActiveChip key={i} label={f.label} onRemove={f.clear} />
            ))}
            <button className="jp-clear-all" onClick={clearAll}>Clear all</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="jp-loading">
            <div className="jp-spinner" />
            <div>Finding opportunities…</div>
          </div>
        )}

        {/* Error */}
        {!loading && fetchError && (
          <div className="jp-error">
            <div>⚠️ {fetchError}</div>
            <button className="jp-btn jp-btn--outline" onClick={fetchJobs}>Retry</button>
          </div>
        )}

        {/* Empty */}
        {!loading && !fetchError && filteredJobs.length === 0 && (
          <div className="jp-empty">
            <div className="jp-empty__icon">
              {quickFilter === "saved" ? "❤️" : quickFilter === "applied" ? "✅" : "🔍"}
            </div>
            <div className="jp-empty__title">
              {quickFilter === "saved" ? "No saved jobs yet"
                : quickFilter === "applied" ? "No applications yet"
                  : quickFilter === "new" ? "No new jobs in last 3 days"
                    : "No jobs found"}
            </div>
            <div className="jp-empty__sub">
              {quickFilter === "saved" ? "Browse jobs and click the bookmark icon to save them"
                : quickFilter === "applied" ? "Start applying and track your progress here"
                  : "Try adjusting your filters or search terms"}
            </div>
            <button className="jp-btn jp-btn--outline" onClick={clearAll}>Clear filters</button>
          </div>
        )}

        {/* Job grid — 3 columns */}
        {!loading && !fetchError && filteredJobs.length > 0 && (
          <>
            <div className="jp-grid">
              {visibleJobs.map(job => (
                <JobCard
                  key={job.id}
                  job={job}
                  savedJobs={savedJobs}
                  appliedJobs={appliedJobs}
                  applyingJobId={applyingJobId}
                  onToggleSave={handleToggleSave}
                  onApplyClick={handleApplyClick}
                  onViewDesc={setSelectedJob}
                />
              ))}
            </div>

            {hasMore && (
              <SeeMoreButton remaining={remaining} onLoadMore={handleLoadMore} loading={seeMoreLoading} />
            )}

            {!hasMore && filteredJobs.length > (isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE) && (
              <div className="jp-all-loaded">
                <span className="jp-all-loaded__line" />
                <span className="jp-all-loaded__text">All {filteredJobs.length} jobs shown</span>
                <span className="jp-all-loaded__line" />
              </div>
            )}
          </>
        )}
      </main>

      {/* ── Overlays ─────────────────────────────────────────────────────── */}
      {selectedJob && (
        <JobModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
          savedJobs={savedJobs}
          appliedJobs={appliedJobs}
          applyingJobId={applyingJobId}
          onToggleSave={handleToggleSave}
          onApplyClick={handleApplyClick}
        />
      )}

      <Login
        isOpen={loginDrawerOpen}
        onClose={() => { setLoginDrawerOpen(false); if (!user) setPendingJob(null); }}
        onLoginSuccess={handleLoginSuccess}
      />

      <ProfileIncompletePopup
        open={profilePopupOpen}
        onClose={handleProfilePopupClose}
        user={user}
        targetJob={pendingJob}
        serverMissing={serverMissing}
      />

      <SuccessPopup
        key={`${successData.job?.id}-${successData.open}`}
        open={successData.open}
        job={successData.job}
        onClose={() => setSuccessData({ open: false, job: null })}
      />

      <SaveToast
        open={toastData.open}
        saved={toastData.saved}
        onClose={() => setToastData(s => ({ ...s, open: false }))}
      />

      <ErrorToast
        open={errorToast.open}
        message={errorToast.message}
        onClose={() => setErrorToast({ open: false, message: "" })}
      />
    </div>
  );
}